import { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../../components/Layout';
import { Card, CardContent } from '../../components/ui/card';
import { toast } from 'sonner';
import { Wrench, Clock, User, Calendar, Cog, Image } from 'lucide-react';
import { LazyPhotoGallery } from '../../components/PhotoComponents';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WORK_TYPES = [
  { value: 'preventivo', label: 'Preventivo', color: 'emerald' },
  { value: 'correctivo', label: 'Correctivo', color: 'amber' },
  { value: 'averia', label: 'Avería', color: 'rose' },
];

export default function ClientWorks() {
  const [works, setWorks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchWorks();
  }, []);

  const fetchWorks = async () => {
    try {
      const response = await axios.get(`${API}/works`);
      setWorks(response.data);
    } catch (error) {
      toast.error('Error al cargar trabajos');
    } finally {
      setLoading(false);
    }
  };

  const getTypeBadge = (type) => {
    const option = WORK_TYPES.find(o => o.value === type);
    const colors = {
      emerald: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
      amber: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
      rose: 'bg-rose-500/10 text-rose-400 border-rose-500/20',
    };
    return (
      <span className={`px-2.5 py-1 rounded-full text-xs font-mono uppercase tracking-wider border ${colors[option?.color || 'emerald']}`}>
        {option?.label || type}
      </span>
    );
  };

  // Group works by month
  const groupedWorks = works.reduce((acc, work) => {
    const date = new Date(work.date);
    const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    const monthLabel = date.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });
    
    if (!acc[monthKey]) {
      acc[monthKey] = { label: monthLabel, works: [] };
    }
    acc[monthKey].works.push(work);
    return acc;
  }, {});

  return (
    <Layout title="Trabajos Realizados">
      <div className="space-y-6 animate-fade-in" data-testid="client-works">
        <p className="text-slate-400">Historial de trabajos de mantenimiento realizados en tu empresa</p>

        {loading ? (
          <div className="text-center py-12 text-slate-500">Cargando...</div>
        ) : works.length === 0 ? (
          <Card className="bg-slate-900/50 border-slate-800">
            <CardContent className="py-12 text-center">
              <Wrench className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400">No hay trabajos registrados</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-8">
            {Object.entries(groupedWorks).map(([key, group]) => (
              <div key={key}>
                <h3 className="font-rajdhani text-lg font-semibold text-slate-300 uppercase tracking-wider mb-4 flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-sky-400" />
                  {group.label}
                </h3>
                <div className="space-y-3">
                  {group.works.map((work) => (
                    <Card key={work.id} className="bg-slate-900/50 border-slate-800 hover:border-slate-700 transition-all">
                      <CardContent className="p-5">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              {getTypeBadge(work.work_type)}
                              {work.machine_name && (
                                <span className="text-sm text-sky-400 flex items-center gap-1">
                                  <Cog className="w-4 h-4" />
                                  {work.machine_name}
                                </span>
                              )}
                            </div>
                            <p className="text-white mb-3">{work.description}</p>
                            <div className="flex items-center gap-4 text-sm text-slate-400">
                              <span className="flex items-center gap-1">
                                <Calendar className="w-4 h-4" />
                                {new Date(work.date).toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'short' })}
                              </span>
                              <span className="flex items-center gap-1 font-mono">
                                <Clock className="w-4 h-4" />
                                {work.start_time} - {work.end_time}
                              </span>
                              <span className="flex items-center gap-1">
                                <User className="w-4 h-4" />
                                {work.technician}
                              </span>
                            </div>
                            {/* Photos */}
                            {work.photo_count > 0 && (
                              <div className="mt-3">
                                <LazyPhotoGallery id={work.id} type="works" count={work.photo_count} />
                              </div>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
